import { useState } from "react";
import { useShovellerSignupContext } from "../../../../context/shovllerSignupFormContext";
import { useDispatch, useSelector } from "react-redux";
import { useRegisterMutation } from "../../../../slices/usersApiSlice";
import { setCredentials } from "../../../../slices/authSlice";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../../base/Loader";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Button } from "../../../../components/ui/button";
import {
  FaEnvelope,
  FaLock,
  FaCity,
  FaMapMarkerAlt,
  FaGlobe,
  FaMailBulk,
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaHome,
} from "react-icons/fa";
import { StepFooterNav } from "../../shared/NavigationFooter";
import { USER_AGREEMENT } from "./agreement";
import { toast } from "sonner";

export function Step4({ nextStep, preStep, currentStep, totalSteps }) {
  const { formData, handleChange, setError, handleNotifyChange } =
    useShovellerSignupContext();

  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [serverError, setServerError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const [register, { isLoading }] = useRegisterMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (userInfo) {
      navigate("/shoveller/stripeOnboard");
    }
  }, [navigate, userInfo]);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Validation function
  const validate = () => {
    let tempErrors = {};

    if (!agreedToTerms) {
      tempErrors.agreedToTerms = "You must agree to the terms and conditions.";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      tempErrors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email)) {
      tempErrors.email = "Please enter a valid email address.";
    }

    // Password validation
    if (!formData.password) {
      tempErrors.password = "Password is required.";
    } else if (formData.password.length < 6) {
      tempErrors.password = "Password must be at least 6 characters long.";
    }

    // Confirm password validation
    if (!confirmPassword) {
      tempErrors.confirmPassword = "Please confirm your password.";
    } else if (formData.password !== confirmPassword) {
      tempErrors.confirmPassword = "Passwords do not match.";
    }

    if (!formData.address) {
      tempErrors.address = "Address is required.";
    }

    if (!formData.city) {
      tempErrors.city = "City is required.";
    }

    if (!formData.province) {
      tempErrors.province = "Province is required.";
    }

    if (!formData.country) {
      tempErrors.country = "Country is required.";
    }

    if (!formData.postalCode) {
      tempErrors.postalCode = "Postal Code is required.";
    }

    if (!formData.firstName) {
      tempErrors.firstName = "First name is required.";
    }

    if (!formData.lastName) {
      tempErrors.lastName = "Last name is required";
    }

    setErrors(tempErrors);

    // Return true if no errors
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (!validate()) {
      return;
    }

    console.log(formData);

    const data = new FormData();
    data.append("userRole", formData.userRole);
    data.append("userName", formData.userName);
    data.append("firstName", formData.firstName);
    data.append("lastName", formData.lastName);
    data.append("phone", formData.phone);
    data.append("address", formData.address);
    data.append("city", formData.city);
    data.append("province", formData.province);
    data.append("country", formData.country);
    data.append("postalCode", formData.postalCode);
    data.append("email", formData.email);
    data.append("latitude", Number(formData.latitude));
    data.append("longitude", Number(formData.longitude));
    data.append("notifyForPosts", formData.notifyForPosts ? "true" : "false");
    data.append("receiveSMS", formData.receiveSMS ? "true" : "false");

    data.append("maxTravelTime", formData.maxTravelTime);
    data.append("hearAboutUs", formData.hearAboutUs);
    data.append(
      "transportation",
      JSON.stringify(formData.transportation || []),
    );

    if (formData.referredBy && formData.referredBy.trim().length > 0) {
      data.append("referredBy", formData.referredBy);
    }

    data.append("password", formData.password);
    data.append("servicesProvide", JSON.stringify(formData.servicesProvide));
    data.append("stripeAccountId", "none");
    data.append("stripeAccountStatus", "pending");
    data.append("chargesEnabled", false);
    data.append("reason", "User hasn't set up Stripe yet");

    if (formData.image) {
      data.append("image", formData.image);
    } else {
      data.append("image", null);
    }

    try {
      setLoading(true);
      const res = await register({
        data,
        hasImage: formData.image != null,
      }).unwrap();
      console.log(res);

      if (res.error) {
        toast(res.error, { description: res.message });
        setError(res.message || res.error);
        setLoading(false);
        return;
      }

      dispatch(setCredentials({ ...res }));
      setLoading(false);

      // Navigate to Step5 (Stripe onboarding)
      navigate("/helper/authorize");
    } catch (err) {
      setError(err?.data?.message || "An unexpected error occurred.");
      setLoading(false);
    }
  };
  if (loading) return <Loader />;

  return (
    <div className="flex flex-col px-6 py-10 max-w-4xl mx-auto">
      {/* Error Alert */}
      {serverError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6 text-center">
          <strong className="font-bold">ServerError: </strong>
          <span className="block sm:inline">{serverError}</span>
        </div>
      )}

      {/* Title */}
      <h2 className="text-2xl font-bold text-gray-900">Account Details</h2>
      <p className="text-gray-600 mb-8">
        Set your login details and address information
      </p>

      <div className="space-y-6">
        {/* Email */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaEnvelope className="w-4 h-4" /> Email
          </Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email for login"
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div className="relative">
          <Label className="flex items-center gap-2 mb-2">
            <FaLock className="w-4 h-4" /> Password
          </Label>
          <Input
            type={passwordVisible ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-gray-500"
            onClick={() => setPasswordVisible(!passwordVisible)}
          >
            {passwordVisible ? <FaEyeSlash /> : <FaEye />}
          </button>
          {errors.password && (
            <p className="text-xs text-red-500 mt-1">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="relative">
          <Label className="flex items-center gap-2 mb-2">
            <FaLock className="w-4 h-4" /> Confirm Password
          </Label>
          <Input
            type={confirmPasswordVisible ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm Password"
          />
          <button
            type="button"
            className="absolute right-3 top-9 text-gray-500"
            onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
          >
            {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
          </button>
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1">
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Address Section */}
        <div className="pt-6 border-t">
          <h3 className="text-lg font-semibold">Address Details</h3>
          <p className="text-gray-500 text-sm mb-4">
            Please provide your address information below.
          </p>
        </div>

        {/* Address */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaHome className="w-4 h-4" /> Address
          </Label>
          <Input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Address"
          />
          {errors.address && (
            <p className="text-xs text-red-500 mt-1">{errors.address}</p>
          )}
        </div>

        {/* City */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaCity className="w-4 h-4" /> City
          </Label>
          <Input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City"
          />
          {errors.city && (
            <p className="text-xs text-red-500 mt-1">{errors.city}</p>
          )}
        </div>

        {/* Province */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaMapMarkerAlt className="w-4 h-4" /> Province
          </Label>
          <Input
            type="text"
            name="province"
            value={formData.province}
            onChange={handleChange}
            placeholder="Province"
          />
          {errors.province && (
            <p className="text-xs text-red-500 mt-1">{errors.province}</p>
          )}
        </div>

        {/* Country */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaGlobe className="w-4 h-4" /> Country
          </Label>
          <Input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            placeholder="Country"
          />
          {errors.country && (
            <p className="text-xs text-red-500 mt-1">{errors.country}</p>
          )}
        </div>

        {/* Postal Code */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <FaMailBulk className="w-4 h-4" /> Postal Code
          </Label>
          <Input
            type="text"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            placeholder="Postal Code"
          />
          {errors.postalCode && (
            <p className="text-xs text-red-500 mt-1">{errors.postalCode}</p>
          )}
        </div>

        {/* User Agreement Section */}
        <div className="pt-6 border-t">
          <div className="mt-3 text-2xl font-medium tracking-wide text-black mb-4">
            User Agreement
          </div>
          <div
            className="overflow-y-scroll h-80 border rounded-lg p-4 text-sm"
            style={{ scrollbarWidth: "thin" }}
          >
            <p>
              <strong>Last Updated: {USER_AGREEMENT.lastUpdated}</strong>
            </p>
            <p className="mt-2">
              This User Agreement ("Agreement") is a legal contract between you
              ("Service Provider," "Helper," "Neighbourhood Helper," "you," or
              "your") and ShovelHouse Inc. ("Shovel House"). By using the Shovel
              House platform as a Service Provider, you agree to comply with and
              be bound by the terms and conditions of this Agreement.
            </p>

            {USER_AGREEMENT.sections.map((section, index) => (
              <p key={index} className="mt-2">
                <strong>{section.title}</strong> <br />
                {section.content && section.content}
                {section.listItems && (
                  <ul className="list-disc ml-5 mt-1">
                    {section.listItems.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.additionalContent && section.additionalContent}
              </p>
            ))}
          </div>

          <div className="pt-4 flex items-center space-x-2 mb-4">
            <Checkbox
              id="agree"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked)}
            />
            <Label htmlFor="agree" className="text-sm">
              I agree to the terms and conditions.
            </Label>
          </div>
          {errors.agreedToTerms && (
            <p className="text-xs text-red-500 mt-1">{errors.agreedToTerms}</p>
          )}

          <div className="flex items-center space-x-2 mb-6">
            <Checkbox
              id="recieveEmailNotifications"
              checked={formData.notifyForPosts}
              onCheckedChange={handleNotifyChange}
            />
            <Label htmlFor="recieveEmailNotifications" className="text-sm">
              Receive email notification when there is a job posting near you.
            </Label>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <StepFooterNav
        currentStep={currentStep}
        totalSteps={totalSteps}
        onBack={preStep}
        onNext={handleSubmit}
        nextDisabled={false}
      />
    </div>
  );
}
